import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux'; // Import useDispatch
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import JobSiteCard from '../components/JobSiteCard';
import { MdOutlineDomainAdd } from "react-icons/md";
import { FaSearch, FaInfoCircle } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';
import { useSubStatus } from '../hooks/useSubStatus'; // Import the subscription hook
import { getJobsites } from '../features/jobsites/jobsitesSlice'; // Import Redux action

const JobSites = () => {
    const dispatch = useDispatch();

    const { jobsites, isLoading, message, isSuccess } = useSelector(state => state.businessJobsites);
    const token = useSelector(state => state.auth.user?.token);

    const [searchQuery, setSearchQuery] = useState('');
    const [isInfoOpen, setIsInfoOpen] = useState(false);
    const [tradeSnapshot, setTradeSnapshot] = useState({});
    const { canAccessFeatures, hasSubscription, subStatus } = useSubStatus();

    const handleNewJobSiteClick = (e) => {
        if (!canAccessFeatures) {
            e.preventDefault();
            toast.error("No active subscription");
        } else if (subStatus === "trialing") {
            toast.info("Reminder: You are on a trial. Please add a payment method soon to avoid interruption.");
        }
    };

    // Fetch job sites when the component mounts
    useEffect(() => {
        dispatch(getJobsites());
    }, [dispatch]);

    // Fetch today's trade snapshot once for all jobsites
    useEffect(() => {
        if (!token) return;
        fetch('/api/dash/todayTradesSnapshot', {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(res => (res.ok ? res.json() : null))
            .then(data => { if (data) setTradeSnapshot(data.byJobsite); })
            .catch(() => {});
    }, [token]);

    // Refetch jobsites after a successful delete
    useEffect(() => {
        if (isSuccess && message === "Jobsite deleted successfully") {
        dispatch(getJobsites());
        }
    }, [isSuccess, message, dispatch]);

    // Close info modal on ESC key
    useEffect(() => {
        if (!isInfoOpen) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setIsInfoOpen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isInfoOpen]);

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const filteredJobSites = jobsites?.filter(jobSite =>
        jobSite.jobsiteName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        jobSite.address.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <>
            <div className="relative flex items-center gap-3 mb-2">
                <button
                    type="button"
                    className="p-2 rounded-md text-ink-3 hover:bg-surface-2 transition-colors"
                    aria-label="Information"
                    title="Information"
                    onClick={() => setIsInfoOpen(true)}
                >
                    <FaInfoCircle className="text-base" />
                </button>

                {/* Search */}
                <div className="flex-1">
                    <div className="relative w-full max-w-md">
                        <input
                            type="text"
                            placeholder="Search jobsites..."
                            value={searchQuery}
                            onChange={handleSearch}
                            className="w-full py-2 pl-9 pr-3 border border-border rounded-lg bg-surface text-body text-ink placeholder:text-ink-4 focus:outline-none focus:ring-2 focus:ring-brand/40 shadow-sm"
                        />
                        <FaSearch className="absolute left-3 top-2.5 text-ink-4 text-sm" />
                    </div>
                </div>

                {/* Add Jobsite — primary button */}
                <Link
                    to="/newJobSite"
                    onClick={handleNewJobSiteClick}
                    className="flex items-center gap-2 rounded-lg bg-ink px-4 py-2 text-label text-white transition hover:bg-ink-2 shadow-sm"
                >
                    <MdOutlineDomainAdd className="text-base" />
                    <span>Add Jobsite</span>
                </Link>
            </div>

            {/* JobSite List */}
            <div className="p-4">
                {isLoading ? (
                    <p>Loading job sites...</p>
                ) : (
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-6 items-start">
                        {filteredJobSites?.map((jobSite) => (
                            <div key={jobSite.id} className="min-w-0">
                                <JobSiteCard
                                    jobSite={jobSite}
                                    tradeData={tradeSnapshot[jobSite.id] ?? null}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Info Modal */}
            {isInfoOpen && (
                <div
                    className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4"
                    onClick={() => setIsInfoOpen(false)}
                    aria-hidden={!isInfoOpen}
                >
                    <div
                        className="bg-surface rounded-xl shadow-md border border-border w-full max-w-lg p-6 relative"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="jobsites-info-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            className="absolute top-3 right-3 p-1 rounded-md text-ink-3 hover:bg-surface-2 transition-colors"
                            aria-label="Close"
                            onClick={() => setIsInfoOpen(false)}
                        >
                            <IoClose className="text-lg" />
                        </button>
                        <h2 id="jobsites-info-title" className="text-card-title text-ink mb-3">Job Sites</h2>
                        <div className="space-y-2 text-body text-ink-2">
                            <p>This page lets you manage your company job sites.</p>
                            <ul className="list-disc pl-5 space-y-1">
                                <li>
                                    Foremen: Workers assigned as foremen get a ★ badge on their job site cards. They are able to clock in other workers at that site, and send onboarding invitations.
                                </li>
                                <li>
                                    Pause: Stopping work temporarily? Pause it. This keeps all data intact. Charges will stop immediately for that jobsite. Workers will not be able to clock in while a jobsite is paused.
                                </li>
                                <li>
                                    Notes: Notes you add are visible to all workers, you can share important details here.
                                </li>
                            </ul>
                            <p className="text-secondary text-ink-3 pt-1">
                                Tip: If you are on a trial, remember to add a payment method to avoid interruptions.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default JobSites;
